import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import Header from "@/components/Header";
import AppIcon from "@/components/AppIcon";
import RatingStars from "@/components/RatingStars";
import InstallGuide from "@/components/InstallGuide";
import SharedAccountModal from "@/components/SharedAccountModal";
import Badge from "@/components/Badge";
import LoadError from "@/components/LoadError";
import { apiErrorMessage } from "@/lib/apiResponses";

function Stat({ value, label }) {
  return (
    <div className="flex-1 text-center">
      <div className="font-display font-bold text-base">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

export default function AppDetailView() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [modal, setModal] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const trackedAppId = useRef("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const controller = new AbortController();
    setApp(null);
    setError(null);
    api
      .app(id, { signal: controller.signal })
      .then((d) => {
        if (!controller.signal.aborted) setApp(d);
      })
      .catch((e) => { if (!controller.signal.aborted) setError(apiErrorMessage(e)); });
    return () => controller.abort();
  }, [id, retry]);

  useEffect(() => {
    if (!app || app === false || trackedAppId.current === String(app.id)) return;
    trackedAppId.current = String(app.id);
    track("view_app", app.name || app.title || "", {
      app_name: app.name || app.title || "",
      app_id: app.id,
    });
  }, [app]);

  if (error) {
    return (
      <div className="min-h-screen">
        <Header title="Приложение" back />
        <div className="px-4"><LoadError message={error} onRetry={() => setRetry((n) => n + 1)} testId="app-detail-error" /></div>
      </div>
    );
  }
  if (!app) return <div className="min-h-screen grid place-items-center text-slate-500" role="status">Загрузка…</div>;

  return (
    <div className="pb-28">
      <Header title={app.name} subtitle={app.developer} back />

      <div className="px-4 pt-5">
        {/* header card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex gap-4">
          <AppIcon src={app.icon} alt={app.name} size={92} className="rounded-[22px] border border-white/10" />
          <div className="min-w-0 flex-1 self-center">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold leading-tight truncate">{app.name}</h1>
              <Badge text={app.badge} />
            </div>
            <div className="text-sm text-slate-400 truncate">{app.subtitle || app.developer}</div>
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
              <RatingStars rating={app.rating} />
              <span className="text-white font-semibold">{app.rating}</span>
            </div>
          </div>
        </motion.div>

        {/* CTAs */}
        <div className="mt-5 flex gap-3">
          <button
            data-testid="get-account-cta"
            onClick={() => setModal(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 active:scale-95 transition shadow-xl shadow-emerald-500/20"
          >
            <Download size={18} /> Получить аккаунт
          </button>
          {app.name === "Сбербанк Онлайн" ? (
            <button
              type="button"
              onClick={() => setModal(true)}
              data-testid="scroll-to-guide"
              className="shrink-0 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold bg-white/10 border border-white/10 active:scale-95 transition"
            >
              <BookOpen size={18} /> Инструкция
            </button>
          ) : (
            <a
              href="#guide"
              data-testid="scroll-to-guide"
              className="shrink-0 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold bg-white/10 border border-white/10 active:scale-95 transition"
            >
              <BookOpen size={18} /> Инструкция
            </a>
          )}
        </div>

        {/* quick stats */}
        <div className="mt-5 flex items-center rounded-2xl bg-white/[0.03] border border-white/10 py-3 divide-x divide-white/10">
          <Stat value={`${app.rating} ★`} label={`${app.ratings_count}`} />
          <Stat value={app.age || "4+"} label="возраст" />
          <Stat value={app.category || "—"} label="категория" />
          <Stat value={app.size || "—"} label="размер" />
        </div>

        {/* screenshots */}
        {app.screenshots?.length > 0 && (
          <div className="mt-6 flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 snap-x">
            {app.screenshots.map((s, i) => (
              <img
                key={i}
                src={s}
                alt={`screenshot ${i + 1}`}
                loading="lazy"
                className="snap-start shrink-0 w-[62%] rounded-2xl border border-white/10 object-cover"
                style={{ aspectRatio: "9/16" }}
              />
            ))}
          </div>
        )}

        {/* description */}
        <section className="mt-6">
          <h3 className="font-display text-lg font-bold mb-2">Описание</h3>
          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{app.description}</p>
          {app.whats_new && (
            <div className="mt-4">
              <h3 className="font-display text-lg font-bold mb-1">Что нового</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{app.whats_new}</p>
            </div>
          )}
        </section>

        {/* install guide */}
        <section id="guide" className="mt-8 scroll-mt-20">
          <h3 className="font-display text-xl font-bold">Как установить</h3>
          <p className="text-sm text-slate-400 mb-4">
            Получите общий аккаунт кнопкой выше и следуйте шагам.
          </p>
          <InstallGuide appName={app.name} />
        </section>
      </div>

      <SharedAccountModal open={modal} onOpenChange={setModal} appName={app.name} />
    </div>
  );
}
